import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Truck, Trash2, Hammer, Home as HomeIcon,
  ShieldCheck, Users, Leaf, Star, ArrowRight, Phone, ChevronRight, Loader2, Package, BadgePercent
} from "lucide-react";
import axios from "axios";
import { useSiteConfig } from "@/context/SiteConfigContext";
import SEOHead from "@/components/SEOHead";
import { websiteSchema, organizationSchema, serviceSchema, offerSchema, faqSchema } from "@/utils/schemas";
import FAQSection, { generalFAQs } from "@/components/FAQSection";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICON_MAP = {
  Truck, Trash2, Hammer, Home: HomeIcon, HomeIcon,
  ShieldCheck, Users, Leaf, Star, Package,
};

const defaultHeroImage = "https://images.unsplash.com/photo-1453176269464-1aa95906d4a2?fm=webp&w=1200&q=60";

const defaultCMSContent = {
  hero: {
    badge: "Firefighter Owned & Operated",
    headline1: "Mission Ready.",
    headline2: "Junk Gone.",
    description: "Professional junk removal, hauling, clean outs, and light demolition services in the DFW metroplex. Reliable. Insured. On time.",
    image: defaultHeroImage,
    mobile_image: "",
  },
  services_section: {
    title: "Our Services",
    subtitle: "What We Do",
    background_image: "",
  },
};

export default function Home() {
  const [testimonials, setTestimonials] = useState([]);
  const [cmsContent, setCmsContent] = useState(null);
  const [body, setBody] = useState(null);
  const [discountLabel, setDiscountLabel] = useState("10%");
  const [cmsLoaded, setCmsLoaded] = useState(false);
  const { formatted_phone, phone_link } = useSiteConfig();

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [testimonialsRes, cmsRes, bodyRes] = await Promise.all([
          axios.get(`${API}/testimonials`).catch(() => ({ data: [] })),
          axios.get(`${API}/cms/page/home`).catch(() => ({ data: [] })),
          axios.get(`${API}/cms/home-body`).catch(() => ({ data: null })),
        ]);

        setTestimonials(testimonialsRes.data?.slice?.(0, 3) || []);

        // Discount label (kept in sync with the Discount Page CMS)
        axios.get(`${API}/cms/page/community-discount`)
          .then((r) => {
            const doc = r.data?.find?.((s) => s.section === "content");
            if (doc?.content?.discount_label) setDiscountLabel(doc.content.discount_label);
          })
          .catch(() => {});

        // Merge CMS hero content
        const content = { ...defaultCMSContent };
        if (cmsRes.data?.length > 0) {
          cmsRes.data.forEach(item => {
            if (item.section && item.content) {
              content[item.section] = { ...content[item.section], ...item.content };
            }
          });
        }
        setCmsContent(content);
        setBody(bodyRes.data);
      } catch {
        setCmsContent(defaultCMSContent);
      } finally {
        setCmsLoaded(true);
      }
    };
    fetchAll();
  }, []);

  const hero = cmsContent?.hero || defaultCMSContent.hero;
  const heroImage = hero.image || defaultHeroImage;
  const servicesSection = cmsContent?.services_section || defaultCMSContent.services_section;
  const trustBadges = body?.trust_badges || [];
  const serviceCards = body?.service_cards || [];
  const whyChooseUs = body?.why_choose_us || {};
  const testimonialsSection = body?.testimonials_section || {};
  const serviceAreas = body?.service_areas || {};
  const ctaSection = body?.cta || {};

  const getIcon = (iconName) => ICON_MAP[iconName] || Package;

  // Use mobile image on small screens if available
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const resolvedHeroImage = (isMobile && hero.mobile_image) ? hero.mobile_image : heroImage;

  const homeSchemas = useMemo(() => {
    const s = [websiteSchema(), organizationSchema()];
    serviceCards.forEach(card => {
      s.push(serviceSchema({ name: card.title, description: card.description, image: card.image }));
    });
    s.push(offerSchema("Free, no-obligation junk removal quote"));
    s.push(faqSchema(generalFAQs));
    return s;
  }, [serviceCards]);

  if (!cmsLoaded) {
    return (
      <div data-testid="home-page" className="pt-20">
        <section className="hero-section relative min-h-[90vh] flex items-center" style={{ backgroundImage: `url(${defaultHeroImage})` }}>
          <div className="hero-overlay absolute inset-0"></div>
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
            <div className="max-w-3xl animate-pulse">
              <div className="h-8 w-64 bg-white/20 rounded mb-6"></div>
              <div className="h-20 w-96 bg-white/20 rounded mb-6"></div>
              <div className="h-6 w-80 bg-white/20 rounded"></div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div data-testid="home-page" className="pt-20">
      <SEOHead schemas={homeSchemas} breadcrumbs={[{ name: "Home", url: "/" }]} />
      {/* Hero Section */}
      <section
        data-testid="hero-section"
        className="hero-section relative min-h-[90vh] flex items-center"
        style={{ backgroundImage: `url(${resolvedHeroImage})` }}
      >
        <div className="hero-overlay absolute inset-0"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-3xl animate-slide-up">
            <span
              className="inline-block bg-[#b51a00] text-white px-4 py-2 text-sm font-bold uppercase tracking-widest mb-6"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              {hero.badge || "Firefighter Owned & Operated"}
            </span>
            <h1
              className="text-5xl md:text-7xl font-black uppercase tracking-wide mb-6 text-white"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              {hero.headline1 || "Mission Ready."}<br />
              <span className="text-[#b51a00]">{hero.headline2 || "Junk Gone."}</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 mb-8 leading-relaxed max-w-2xl">
              {hero.description}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact" aria-label="Get a free junk removal quote">
                <Button
                  data-testid="hero-quote-btn"
                  className="bg-[#b51a00] hover:bg-[#8a1300] text-white font-bold uppercase tracking-wider rounded-sm px-8 py-6 text-lg shadow-lg hover:-translate-y-1 transition-all"
                >
                  Get a Free Quote
                  <ArrowRight className="ml-2 w-5 h-5" aria-hidden="true" />
                </Button>
              </Link>
              <a href={phone_link} aria-label={`Call Overhaul Ops at ${formatted_phone}`}>
                <Button
                  data-testid="hero-call-btn"
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white hover:text-[#0f0f0f] font-bold uppercase tracking-wider rounded-sm px-8 py-6 text-lg"
                >
                  <Phone className="mr-2 w-5 h-5" />
                  {formatted_phone}
                </Button>
              </a>
            </div>

            {/* Community discount ribbon — synced with Discount Page CMS */}
            <Link
              to="/community-appreciation-discount"
              data-testid="hero-discount-ribbon"
              className="group mt-6 inline-flex items-center gap-3 rounded-full bg-white/10 hover:bg-white/15 border border-white/20 backdrop-blur-sm pl-4 pr-5 py-2.5 transition-colors"
            >
              <span className="inline-flex items-center gap-1.5 rounded-full bg-[#b51a00] text-white text-xs font-bold uppercase tracking-wider px-2.5 py-1">
                <BadgePercent className="w-3.5 h-3.5" />
                {discountLabel} Off
              </span>
              <span className="text-sm text-gray-200 font-medium">
                for Military, First Responders, Nurses, Teachers &amp; Seniors
              </span>
              <ArrowRight className="w-4 h-4 text-white transition-transform group-hover:translate-x-1" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>

      {/* Trust Bar - Dynamic */}
      {trustBadges.length > 0 && (
        <section data-testid="trust-bar" className="bg-[#1a1a1a] py-6 noise-texture">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-wrap justify-center gap-8 md:gap-16">
              {trustBadges.map((badge, index) => {
                const BadgeIcon = getIcon(badge.icon);
                return (
                  <div key={index} className="flex items-center gap-3">
                    <BadgeIcon className="w-6 h-6 text-[#FFD700]" />
                    <span
                      className="text-white font-bold uppercase tracking-wider text-sm"
                      style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                    >
                      {badge.text}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Services Preview - Dynamic */}
      {serviceCards.length > 0 && (
        <section data-testid="services-section" className="py-20 md:py-32 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <span className="text-[#b51a00] font-bold uppercase tracking-widest text-sm">
                {servicesSection.subtitle || "What We Do"}
              </span>
              <h2
                className="text-4xl md:text-5xl font-bold uppercase tracking-tight mt-2 text-[#0f0f0f]"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              >
                {servicesSection.title || "Our Services"}
              </h2>
              <div className="section-divider mx-auto mt-4"></div>
            </div>

            <div className="bento-grid">
              {serviceCards.map((service, index) => {
                const ServiceIcon = getIcon(service.icon);
                const imgSrc = service.image?.startsWith("/")
                  ? `${process.env.REACT_APP_BACKEND_URL}${service.image}`
                  : service.image;
                return (
                  <Card
                    key={index}
                    data-testid={`service-card-${index}`}
                    className="service-card bg-white border border-gray-200 hover:border-[#b51a00] rounded-sm overflow-hidden group"
                  >
                    <div className="aspect-video overflow-hidden">
                      {imgSrc && (
                        <img
                          src={imgSrc}
                          alt={service.alt || service.title}
                          width="400"
                          height="225"
                          loading="lazy"
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      )}
                    </div>
                    <CardContent className="p-6">
                      <div className="icon-box rounded-sm mb-4">
                        <ServiceIcon className="w-7 h-7" />
                      </div>
                      <h3
                        className="text-2xl font-bold uppercase tracking-tight mb-3 text-[#0f0f0f]"
                        style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                      >
                        {service.title}
                      </h3>
                      <p className="text-[#4a4a4a] leading-relaxed">
                        {service.description}
                      </p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            <div className="text-center mt-12">
              <Link to="/services">
                <Button
                  data-testid="view-services-btn"
                  className="bg-[#b51a00] hover:bg-[#8a1300] text-white font-bold uppercase tracking-wider rounded-sm px-8 py-4"
                >
                  View All Services
                  <ChevronRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Why Choose Us - Dynamic */}
      {whyChooseUs.headline && (
        <section data-testid="why-us-section" className="py-20 md:py-32 bg-[#f4f4f5]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className="text-[#b51a00] font-bold uppercase tracking-widest text-sm">
                  {whyChooseUs.label || "Why Overhaul Ops"}
                </span>
                <h2
                  className="text-4xl md:text-5xl font-bold uppercase tracking-tight mt-2 mb-6 text-[#0f0f0f]"
                  style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                >
                  {whyChooseUs.headline}
                </h2>
                <div className="section-divider mb-8"></div>
                <p className="text-lg text-[#4a4a4a] leading-relaxed mb-8">
                  {whyChooseUs.description}
                </p>
                <ul className="space-y-4">
                  {(whyChooseUs.bullets || []).map((item, index) => (
                    <li key={index} className="flex items-center gap-3">
                      <ShieldCheck className="w-5 h-5 text-[#b51a00]" />
                      <span className="text-[#0f0f0f] font-medium">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="relative">
                {whyChooseUs.image && (
                  <img
                    src={whyChooseUs.image.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${whyChooseUs.image}` : whyChooseUs.image}
                    alt="Firefighter owned junk removal company DFW"
                    className="w-full rounded-sm shadow-2xl"
                    width="600"
                    height="400"
                    loading="lazy"
                  />
                )}
                {whyChooseUs.badge_text && (
                  <div className="absolute -bottom-6 -left-6 bg-[#b51a00] text-white p-6 rounded-sm shadow-lg">
                    <span
                      className="text-4xl font-black block"
                      style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                    >
                      {whyChooseUs.badge_text}
                    </span>
                    <span className="text-sm uppercase tracking-wider">{whyChooseUs.badge_subtext}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Testimonials Preview */}
      {testimonials.length > 0 && (
        <section data-testid="testimonials-preview" className="py-20 md:py-32 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <span className="text-[#b51a00] font-bold uppercase tracking-widest text-sm">
                {testimonialsSection.label || "Customer Reviews"}
              </span>
              <h2
                className="text-4xl md:text-5xl font-bold uppercase tracking-tight mt-2 text-[#0f0f0f]"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              >
                {testimonialsSection.headline || "What People Say"}
              </h2>
              <div className="section-divider mx-auto mt-4"></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {testimonials.map((testimonial, index) => (
                <div
                  key={testimonial.id || index}
                  data-testid={`testimonial-card-${index}`}
                  className="testimonial-card"
                >
                  <div className="star-rating flex gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-current" />
                    ))}
                  </div>
                  <p className="text-[#4a4a4a] leading-relaxed mb-6 italic">
                    "{testimonial.text}"
                  </p>
                  <div>
                    <p className="font-bold text-[#0f0f0f]">{testimonial.name}</p>
                    <p className="text-sm text-[#707070]">{testimonial.location}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="text-center mt-12">
              <Link to="/testimonials">
                <Button
                  variant="outline"
                  className="border-2 border-[#b51a00] text-[#b51a00] hover:bg-[#b51a00] hover:text-white font-bold uppercase tracking-wider rounded-sm px-8 py-4"
                >
                  Read More Reviews
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Service Areas - Dynamic */}
      {serviceAreas.areas?.length > 0 && (
        <section data-testid="service-areas" className="py-20 md:py-32 bg-[#1a1a1a] text-white noise-texture">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <span className="text-[#FFD700] font-bold uppercase tracking-widest text-sm">
                {serviceAreas.label || "Coverage Area"}
              </span>
              <h2
                className="text-4xl md:text-5xl font-bold uppercase tracking-tight mt-2"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              >
                {serviceAreas.headline || "Serving DFW Metroplex"}
              </h2>
              <div className="section-divider mx-auto mt-4 bg-gradient-to-r from-[#FFD700] to-transparent"></div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {serviceAreas.areas.map((area, index) => (
                <Link
                  key={index}
                  to={`/locations/${area.slug}`}
                  aria-label={`Junk removal services in ${area.name}`}
                  className="bg-white/5 border border-white/10 p-6 text-center rounded-sm hover:bg-white/10 hover:border-[#b51a00] transition-all cursor-pointer"
                >
                  <span
                    className="text-xl font-bold uppercase tracking-wider text-white"
                    style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                  >
                    {area.name}
                  </span>
                </Link>
              ))}
            </div>

            <div className="text-center mt-8">
              <Link to="/locations" aria-label="View all service areas" className="text-[#FFD700] hover:text-white font-medium transition-colors">
                View All Service Areas
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* FAQ Section */}
      <FAQSection
        faqs={generalFAQs}
        title="Junk Removal FAQs"
        subtitle="Got questions about our junk removal services in DFW? We've got answers."
      />

      {/* CTA Section - Dynamic */}
      <section data-testid="cta-section" className="py-20 bg-[#b51a00]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2
            className="text-4xl md:text-5xl font-bold uppercase tracking-wide text-white mb-6"
            style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
          >
            {ctaSection.headline || "Ready to Reclaim Your Space?"}
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            {ctaSection.description || "Get a free, no-obligation quote today. Fast response, fair pricing, professional service."}
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/contact">
              <Button
                data-testid="cta-quote-btn"
                className="bg-white text-[#b51a00] hover:bg-gray-100 font-bold uppercase tracking-wider rounded-sm px-8 py-6 text-lg"
              >
                Get Your Free Quote
              </Button>
            </Link>
            <a href={phone_link}>
              <Button
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-[#b51a00] font-bold uppercase tracking-wider rounded-sm px-8 py-6 text-lg"
              >
                <Phone className="mr-2 w-5 h-5" />
                Call Now
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
